import { WorkwearExpressBundleDataOptionSelectionQtyInterface } from 'swagger/ts/Definitions';
import camelcaseKeys from 'camelcase-keys';
import { FormikValuesType } from 'src/MixAndMatchBundle/MixAndMatchBundle';
import { camelized_wearerBundleDetail_wearerBundleDetail_WearerBundlesProduct_bundle_items } from 'src/queries/__generated__/wearerBundleDetail';
import { HAVE_MY_LOGO } from 'src/constants/artwork';
import { SelectedOptions } from 'src/components/BundleProduct/types/index';

const getCustomisationData = (
    {
        selectedOptions,
        selectedMethod,
        selectedArtwork,
    }: { selectedOptions?: SelectedOptions; selectedMethod?: string; selectedArtwork?: string },
    index: number,
) => {
    const defaultData = {
        customisationName: '',
        type: parseInt(selectedMethod!),
        specialInstructions: selectedOptions?.specialInstructions || '',
        isFree: index === 0,
        isArtworkMissing: selectedArtwork === 'logoExist',
        image: '',
        isContactRequested: false,
        textLineOne: '',
        textLineTwo: '',
        textLineThree: '',
        color: '',
        font: '',
    };
    if ('textLineOne' in selectedOptions! && 'image' in selectedOptions) {
        return {
            ...defaultData,
            id: selectedOptions.id,
            artworkId: selectedOptions.artworkId,
            isContactRequested: selectedOptions.isContactRequested,
            textLineOne: selectedOptions.textLineOne,
            textLineTwo: selectedOptions.textLineTwo || defaultData.textLineTwo,
            textLineThree: selectedOptions.textLineThree || defaultData.textLineThree,
            color: selectedOptions.color,
            font: selectedOptions.font,
            image: selectedOptions.image,
            customisationName: selectedOptions.customisationName,
            type: selectedOptions.type,
        };
    } else if ('textLineOne' in selectedOptions!) {
        return {
            ...defaultData,
            textLineOne: selectedOptions.textLineOne,
            textLineTwo: selectedOptions.textLineTwo || defaultData.textLineTwo,
            textLineThree: selectedOptions.textLineThree || defaultData.textLineThree,
            color: selectedOptions.color,
            font: selectedOptions.font,
        };
    } else if ('image' in selectedOptions!) {
        return {
            ...defaultData,
            image: selectedOptions.image || defaultData.image,
        };
    } else if ('isContactRequested' in selectedOptions!) {
        return {
            ...defaultData,
            isContactRequested: ['contact', HAVE_MY_LOGO].includes(selectedOptions.isContactRequested) ? true : false,
            is_artwork_missing: selectedOptions.isContactRequested === HAVE_MY_LOGO,
        };
    } else {
        return defaultData;
    }
};

export const convertFormDataToPayload = (
    data: FormikValuesType,
    sku: string,
    bundles: camelized_wearerBundleDetail_wearerBundleDetail_WearerBundlesProduct_bundle_items[],
    wearerBundleData: any,
) => {
    const bundleCustomisation = data.customisations.map((customisation, index) => {
        if (customisation.selectedArtworkExisting) {
            return {
                location: customisation?.selectedPositions?.[0] || '',
                customisation: {
                    ...camelcaseKeys(JSON.parse(customisation.selectedArtworkExisting), { deep: true }),
                    isFree: index === 0,
                },
            };
        } else {
            return {
                location: customisation?.selectedPositions?.[0] || '',
                customisation: getCustomisationData(customisation, index),
            };
        }
    });

    const optionSelections = (bundle, index: number, selectedBundleVariants) => {
        const optionSelections: number[] = [];
        const optionSelectionsQty: WorkwearExpressBundleDataOptionSelectionQtyInterface[] = [];
        const bundleProducts = bundle.products;

        bundleProducts.forEach((bundleProduct) =>
            bundleProduct.selectedColorsAndSizes.forEach((colorAndSize) => {
                const variantsOfBundle = selectedBundleVariants.find((variant) => variant.name === bundleProduct.name);

                const variantsOfColor = variantsOfBundle.variants.filter(
                    (variant) =>
                        variant.attributes.find((attribute) => attribute.code === 'color').valueIndex ===
                        colorAndSize.colorId,
                );

                const variant = variantsOfColor.find(
                    (variant) =>
                        variant.attributes.find((attribute) => attribute.code === 'size').valueIndex ===
                        colorAndSize.sizes.id,
                );

                if (variant) {
                    const simpleSku = variant.product.sku;

                    const correctWearerBundleData = wearerBundleData.find(
                        (dataItem) => dataItem?.bundleInformation?.name === bundleProduct.name,
                    );

                    const correctOption = correctWearerBundleData?.bundleOptions?.items?.[simpleSku];

                    optionSelections.push(correctOption?.optionItemID);
                    optionSelectionsQty.push({
                        selection_id: correctOption?.optionItemID,
                        selection_qty: colorAndSize.sizes.quantity,
                    });
                }
            }),
        );

        return {
            optionSelections,
            optionSelectionsQty,
        };
    };

    const bundleOptionsData = data.bundles.map((formikBundle, index) => {
        const correctBundleItems = bundles.find((bundle) => bundle.optionId === formikBundle.id)?.targetConfigurable;

        const selectedBundleVariants = formikBundle.products.map((product) =>
            correctBundleItems?.find((item) => item.name === product.name),
        );

        return {
            extension_attributes: {
                option_selections_qty: optionSelections(formikBundle, index, selectedBundleVariants)
                    .optionSelectionsQty,
            },
            option_id: formikBundle.id,
            option_qty: 1,
            option_selections: optionSelections(formikBundle, index, selectedBundleVariants).optionSelections,
        };
    });

    return {
        quote_id: '-1',
        sku,
        qty: 1,
        product_option: {
            extension_attributes: {
                bundled_customisation: bundleCustomisation,
                bundle_options: bundleOptionsData,
            },
        },
    };
};
